/**
 * Shared UI component for UI Topbar.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';


import { UiButtonComponent, UiCardComponent, UiSelectComponent, UiSelectOption } from '@shared/ui';
import { ThemeService } from '../../../core/services/theme.service';
import { AppNotification, AppNotificationType, NotificationService } from '../../../core/services/notification.service';

import { BreadcrumbItem, UiTopbarLanguage, UiTopbarUser } from './ui-topbar.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

interface UiTopbarNotification {
  id: string;
  title: string;
  description: string;
  meta: string;
  type: AppNotificationType;
  read: boolean;
}

type UiTopbarNotificationTab = 'unread' | 'read';

/**
 * COMPONENT: Ui Topbar
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-topbar',
  standalone: true,
  imports: [RouterLink, UiSelectComponent, UiButtonComponent, UiCardComponent, TranslatePipe, UiLabelTranslatePipe],
  templateUrl: './ui-topbar.component.html',
  styleUrls: ['./ui-topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiTopbarComponent implements OnInit {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly themeService = inject(ThemeService);
  private readonly notificationService = inject(NotificationService);
  private readonly notificationPageSize = 15;
  private readonly notificationApiLimit = 50;
  private readonly locallyReadNotificationIds = new Set<string>();
  private readonly pendingReadNotificationIds = new Set<string>();
  readonly breadcrumbs = input<BreadcrumbItem[]>([{ label: 'Dashboard', path: '/dashboard' }]);
  readonly user = input<UiTopbarUser>({
    name: 'Demo User',
    initials: 'DU',
    avatar: '',
    language: 'en'
  });
  readonly themeInput = input<'light' | 'dark'>('light');

  readonly profileClicked = output<void>();
  readonly logoutClicked = output<void>();
  readonly languageChanged = output<UiTopbarLanguage>();
  readonly backClicked = output<void>();
  readonly themeChanged = output<'light' | 'dark'>();

  /** STATE OWNER: menuOpen documents the signal state boundary used by this file. */
  readonly menuOpen = signal(false);
  /** STATE OWNER: notificationDrawerOpen documents the signal state boundary used by this file. */
  readonly notificationDrawerOpen = signal(false);
  /** STATE OWNER: loadedAvatarSrc documents the signal state boundary used by this file. */
  readonly loadedAvatarSrc = signal('');
  /** STATE OWNER: theme documents the computed state boundary used by this file. */
  readonly theme = computed(() => this.themeService.theme());
  readonly activeNotificationTab = signal<UiTopbarNotificationTab>('unread');
  /** STATE OWNER: notificationLimit documents the signal state boundary used by this file. */
  readonly notificationLimit = signal(this.notificationPageSize);
  readonly notifications = signal<UiTopbarNotification[]>([]);
  /** STATE OWNER: unreadNotificationCount documents the computed state boundary used by this file. */
  readonly unreadNotificationCount = computed(() => this.notifications().filter((item) => !item.read).length);
  /** STATE OWNER: filteredNotifications documents the computed state boundary used by this file. */
  readonly filteredNotifications = computed(() => {
    const tab = this.activeNotificationTab();

    if (tab === 'read') {
      return this.notifications().filter((item) => item.read);
    }

    return this.notifications().filter((item) => !item.read);
  });
  /** STATE OWNER: visibleNotifications documents the computed state boundary used by this file. */
  readonly visibleNotifications = computed(() => this.filteredNotifications().slice(0, this.notificationLimit()));
  /** STATE OWNER: hasMoreNotifications documents the computed state boundary used by this file. */
  readonly hasMoreNotifications = computed(() => this.notificationLimit() < this.filteredNotifications().length);
  /** STATE OWNER: unreadNotificationBadge documents the computed state boundary used by this file. */
  readonly unreadNotificationBadge = computed(() => {
    const count = this.unreadNotificationCount();
    return count > 99 ? '99+' : String(count);
  });
  readonly languageOptions: UiSelectOption[] = [
    { label: 'English', value: 'en' },
    { label: 'French', value: 'fr' }
  ];

  /**
   * BUSINESS LOGIC: ngOnInit
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  ngOnInit(): void {
    void this.loadNotifications();
  }

  @HostListener('document:keydown.escape')
  /**
   * BUSINESS LOGIC: onEscapePress
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onEscapePress(): void {
    this.closeNotifications();
    this.closeMenu();
  }

  @HostListener('document:click', ['$event'])
  /**
   * BUSINESS LOGIC: onDocumentClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onDocumentClick(event: MouseEvent): void {
    if (!event.target) {
      return;
    }

    const target = event.target as Node;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.closeMenu();
      this.closeNotifications();
    }
  }

  /**
   * BUSINESS LOGIC: toggleMenu
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
  }

  /**
   * BUSINESS LOGIC: closeMenu
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  closeMenu(): void {
    this.menuOpen.set(false);
  }

  /**
   * BUSINESS LOGIC: toggleTheme
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleTheme(): void {
    const newTheme = this.theme() === 'light' ? 'dark' : 'light';
    this.themeService.setTheme(newTheme);
    this.themeChanged.emit(newTheme);
  }

  /**
   * BUSINESS LOGIC: onProfileClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onProfileClicked(): void {
    this.profileClicked.emit();
    this.closeMenu();
  }

  /**
   * BUSINESS LOGIC: onBackClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onBackClicked(): void {
    this.backClicked.emit();
  }

  /**
   * BUSINESS LOGIC: parseCrumbPath
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  parseCrumbPath(path: string): string {
    return path.split('?')[0];
  }

  /**
   * BUSINESS LOGIC: parseCrumbQuery
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  parseCrumbQuery(path: string): Record<string, string> | null {
    const qs = path.split('?')[1];
    if (!qs) return null;
    const params: Record<string, string> = {};
    qs.split('&').forEach((pair) => {
      const [key, value] = pair.split('=');
      if (key) params[decodeURIComponent(key)] = decodeURIComponent(value ?? '');
    });
    return params;
  }

  /**
   * BUSINESS LOGIC: onLanguageChange
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onLanguageChange(value: string): void {
    if (value === 'en' || value === 'fr') {
      this.languageChanged.emit(value);
    }
  }

  /**
   * BUSINESS LOGIC: onLogoutClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onLogoutClicked(): void {
    this.logoutClicked.emit();
    this.closeMenu();
  }

  /**
   * BUSINESS LOGIC: isAvatarLoaded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isAvatarLoaded(src?: string): boolean {
    const avatarSrc = src?.trim() ?? '';
    return !!avatarSrc && this.loadedAvatarSrc() === avatarSrc;
  }

  /**
   * BUSINESS LOGIC: onAvatarLoaded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onAvatarLoaded(): void {
    this.loadedAvatarSrc.set(this.user().avatar?.trim() ?? '');
  }

  /**
   * BUSINESS LOGIC: onAvatarError
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onAvatarError(): void {
    this.loadedAvatarSrc.set('');
  }

  /**
   * BUSINESS LOGIC: toggleNotifications
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleNotifications(): void {
    this.notificationDrawerOpen.set(!this.notificationDrawerOpen());
    if (this.notificationDrawerOpen()) {
      this.setNotificationTab('unread');
      void this.loadNotifications();
    }
  }

  /**
   * BUSINESS LOGIC: closeNotifications
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  closeNotifications(): void {
    this.notificationDrawerOpen.set(false);
  }

  /**
   * BUSINESS LOGIC: setNotificationTab
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  setNotificationTab(tab: UiTopbarNotificationTab): void {
    this.activeNotificationTab.set(tab);
    this.notificationLimit.set(this.notificationPageSize);
  }

  /**
   * BUSINESS LOGIC: markNotificationAsRead
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async markNotificationAsRead(id: string): Promise<void> {
    // BUSINESS LOGIC: notifications are always expanded; clicking the visible card is the read action.
    const current = this.notifications().find((item) => item.id === id);
    if (!current || current.read || this.pendingReadNotificationIds.has(id)) {
      return;
    }

    this.pendingReadNotificationIds.add(id);
    this.locallyReadNotificationIds.add(id);
    this.notifications.update((items) => items.map((item) => (item.id === id ? { ...item, read: true } : item)));

    const detail = await this.notificationService.markNotificationAsRead(id).catch(() => null);
    this.pendingReadNotificationIds.delete(id);

    if (!detail) {
      return;
    }

    this.notifications.update((items) =>
      items.map((item) => (item.id === id ? { ...this.toTopbarNotification(detail), read: true } : item))
    );
  }

  /**
   * BUSINESS LOGIC: markAllAsRead
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async markAllAsRead(): Promise<void> {
    // Backend owns the durable read state; update the drawer optimistically so the badge clears immediately.
    await this.notificationService.markAllAsRead().catch(() => undefined);
    this.notifications().forEach((item) => this.locallyReadNotificationIds.add(item.id));
    this.notifications.update((items) => items.map((item) => ({ ...item, read: true })));
  }

  /**
   * BUSINESS LOGIC: loadMoreNotifications
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  loadMoreNotifications(event?: Event): void {
    event?.stopPropagation();
    this.notificationLimit.update((limit) => Math.min(limit + this.notificationPageSize, this.filteredNotifications().length));
  }

  /**
   * BUSINESS LOGIC: loadNotifications
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private async loadNotifications(): Promise<void> {
    const result = await this.notificationService
      .listNotifications({ channel: 'push', page: 1, limit: this.notificationApiLimit })
      .catch(() => null);

    if (!result) {
      return;
    }

    this.notifications.set(result.items.map((item) => this.toTopbarNotification(item)));
  }

  /**
   * BUSINESS LOGIC: toTopbarNotification
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toTopbarNotification(item: AppNotification): UiTopbarNotification {
    return {
      id: item.id,
      title: item.title,
      description: item.description,
      meta: item.meta,
      type: item.type,
      read: item.read || this.locallyReadNotificationIds.has(item.id)
    };
  }
}
